import { Component, computed, effect, inject, input, output, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

export interface KeyValueEditorRow {
  key: string;
  value: string;
  isSecret: boolean;
  isAutoGenerated: boolean;
}

@Component({
  selector: 'era-key-value-edit',
  templateUrl: './key-value-edit.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ReactiveFormsModule, FaIconComponent],
})
export class KeyValueEditorComponent {
  private readonly fb = inject(FormBuilder);
  private readonly maskedSecretGroups = new WeakSet<FormGroup>();

  readonly allowEditing = input(false);
  readonly keyValueSet = input<KeyValueEditorRow[]>([]);
  readonly keyValueSetChanged = output<KeyValueEditorRow[]>();

  public form: FormGroup<{ pairs: FormArray<FormGroup> }> = new FormGroup({
    pairs: new FormArray<FormGroup>([]),
  });

  public readonly editMode = computed(() => this.allowEditing());

  get formArray(): FormArray<FormGroup> {
    return this.form.controls.pairs;
  }

  get controls(): FormGroup[] {
    return this.formArray.controls;
  }

  constructor() {
    effect(() => {
      const keyValueSet = this.keyValueSet() ?? [];
      const editable = this.editMode();

      this.formArray.clear();
      keyValueSet.forEach((row) => {
        const group = this.buildGroup(row);
        if (!editable) {
          group.disable({ emitEvent: false });
        }

        this.formArray.push(group);
      });

      if (editable && this.formArray.length === 0) {
        this.formArray.push(this.buildGroup());
      }
    });

    effect(() => {
      if (this.editMode()) {
        this.form.enable();
      } else {
        this.form.disable();
      }
    });
  }

  private buildGroup(row: Partial<KeyValueEditorRow> = {}): FormGroup {
    const group = this.fb.group({
      key: [row.key ?? '', Validators.required],
      value: [row.value ?? ''],
      isSecret: [row.isSecret ?? false],
      isAutoGenerated: [row.isAutoGenerated ?? false],
    });

    if (row.isSecret && row.value === null) {
      this.maskedSecretGroups.add(group);
    }

    return group;
  }
  isAutoGeneratedRow(index: number): boolean {
    return !!this.controls[index]?.get('isAutoGenerated')?.value;
  }

  addPair() {
    this.formArray.push(this.buildGroup());
  }

  removePair(index: number) {
    this.formArray.removeAt(index);
  }

  submit() {
    this.emitChanges();
  }

  private emitChanges() {
    const rows = this.formArray.controls.map((group) => this.normalizeRow(group)).filter((row) => !!row.key);

    this.keyValueSetChanged.emit(rows);
  }

  isSecretRow(index: number): boolean {
    return !!this.controls[index]?.get('isSecret')?.value;
  }

  getValueInputType(index: number): string {
    return this.isSecretRow(index) ? 'password' : 'text';
  }

  getValuePlaceholder(index: number): string {
    if (this.isAutoGeneratedRow(index)) {
      return 'Auto-generated value';
    }
    return this.isSecretRow(index) ? 'Stored secret' : 'Value';
  }

  private normalizeRow(group: FormGroup): KeyValueEditorRow {
    const rawValue = group.getRawValue();
    const rawRowValue = rawValue.value ?? '';
    const normalizedValue =
      rawValue.isSecret && rawRowValue === '' && this.maskedSecretGroups.has(group) ? null : rawRowValue;

    return {
      key: rawValue.key ?? '',
      value: normalizedValue,
      isSecret: rawValue.isSecret ?? false,
      isAutoGenerated: rawValue.isAutoGenerated ?? false,
    };
  }
}
